import { useMemo, useState } from 'react'
import { Alert, Button, DatePicker, InputNumber, Modal, Table, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useToast } from '../../contexts/ToastContext'
import { useAuthorization } from '../../contexts/AuthorizationContext'
import { useAuthSession } from '../../lib/session'
import { permissions, UserTypes } from '../../types/authorization'
import type { SurveyFormField, SurveyFormSubField, SurveyFormSubmission } from '../../types/surveyForm'
import {
  useSurveyFormList,
  useSaveSurveyFormMutation,
  useUpdateSurveyFormMutation,
  useDeleteSurveyFormMutation,
} from '../../hooks/useSurveyForm'
import { useSurveyFormQuestions } from '../../hooks/useSurveyFormQuestions'

dayjs.extend(utc)

type SurveyAnswerValue = number | ''
type SurveyAnswers = Record<string, SurveyAnswerValue>
type NumberFieldConfig = Pick<SurveyFormField | SurveyFormSubField, 'allowNegative' | 'decimalPlaces' | 'symbol'>

function formatDate(value: string | null): string {
  if (!value) return '-'
  const d = dayjs.utc(value).local()
  return d.isValid() ? d.format('MMM D, YYYY') : '-'
}

function payloadKeyForField(field: SurveyFormField) {
  return field.label
}

function payloadKeyForSubField(field: SurveyFormField, subField: SurveyFormSubField) {
  return `${field.label} - ${subField.label}`
}

function buildEmptyAnswers(fields: SurveyFormField[]): SurveyAnswers {
  return fields.reduce<SurveyAnswers>((acc, field) => {
    if (field.subFields) {
      field.subFields.forEach((subField) => {
        acc[payloadKeyForSubField(field, subField)] = ''
      })
    } else {
      acc[payloadKeyForField(field)] = ''
    }
    return acc
  }, {})
}

function readNumericAnswer(value: string | number | undefined): SurveyAnswerValue {
  if (value === undefined || value === null || value === '') return ''
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : ''
}

function buildAnswersFromSubmission(row: SurveyFormSubmission, fields: SurveyFormField[]): SurveyAnswers {
  const answers = buildEmptyAnswers(fields)
  fields.forEach((field) => {
    if (field.subFields) {
      field.subFields.forEach((subField) => {
        const key = payloadKeyForSubField(field, subField)
        answers[key] = readNumericAnswer(row.jsonData[key])
      })
    } else {
      const key = payloadKeyForField(field)
      answers[key] = readNumericAnswer(row.jsonData[key])
    }
  })
  return answers
}

function decimalCount(value: number) {
  const text = String(value)
  if (text.includes('e-')) {
    const [, exponent] = text.split('e-')
    return Number(exponent) || 0
  }
  return text.includes('.') ? (text.split('.')[1] ?? '').length : 0
}

function isValidNumericAnswer(value: SurveyAnswerValue, config: NumberFieldConfig): boolean {
  if (value === '' || !Number.isFinite(value)) return false
  if (config.allowNegative === false && value < 0) return false
  const decimalPlaces = config.decimalPlaces ?? 0
  if (decimalPlaces === 0 && !Number.isInteger(value)) return false
  return decimalCount(value) <= decimalPlaces
}

function getValidationStatus(
  value: SurveyAnswerValue,
  config: NumberFieldConfig,
  showErrors: boolean
): 'error' | undefined {
  return showErrors && !isValidNumericAnswer(value, config) ? 'error' : undefined
}

function getNumberAddons(config: NumberFieldConfig) {
  return {
    addonBefore: config.symbol === '$' ? '$' : undefined,
    addonAfter: config.symbol === '%' ? '%' : undefined,
  }
}

export default function ClientSurveyFormPage({ clientIdOverride }: { clientIdOverride?: string }) {
  const { clientId: activeClientId, hasPermission } = useAuthorization()
  const clientId = clientIdOverride || activeClientId
  const { addToast } = useToast()
  const session = useAuthSession()

  const canSurveyEdit = hasPermission(permissions.editSurveyForm)
  const canSurveyAdd = canSurveyEdit
  const clientName = session?.currentClientName || '-'

  const { data: formFields = [], isLoading: questionsLoading, isError: questionsError, refetch: refetchQuestions } = useSurveyFormQuestions()
  const { data: submissions = [], isLoading, isError, refetch: refetchSubmissions } = useSurveyFormList(clientId)
  const saveMutation = useSaveSurveyFormMutation(clientId)
  const updateMutation = useUpdateSurveyFormMutation(clientId)
  const deleteMutation = useDeleteSurveyFormMutation(clientId)

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedRow, setSelectedRow] = useState<SurveyFormSubmission | null>(null)
  const [monthEndDate, setMonthEndDate] = useState<dayjs.Dayjs | null>(null)
  const [answers, setAnswers] = useState<SurveyAnswers>({})
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const isEdit =
    !!selectedRow &&
    canSurveyEdit &&
    (session?.currentUserId === selectedRow.insertUserId ||
      session?.currentUserTypeId === UserTypes.Admin ||
      session?.currentUserTypeId === UserTypes.ClientAdmin)
  const isNew = !selectedRow && canSurveyAdd

  function openNewModal() {
    setSelectedRow(null)
    setMonthEndDate(null)
    setAnswers(buildEmptyAnswers(formFields))
    setShowValidationErrors(false)
    setModalOpen(true)
  }

  function openRowModal(row: SurveyFormSubmission) {
    setSelectedRow(row)
    setMonthEndDate(row.monthEndDate ? dayjs.utc(row.monthEndDate).local() : null)
    setAnswers(buildAnswersFromSubmission(row, formFields))
    setShowValidationErrors(false)
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setSelectedRow(null)
    setDeleteConfirmOpen(false)
    setShowValidationErrors(false)
  }

  function updateAnswer(key: string, value: number | string | null) {
    setAnswers((current) => ({
      ...current,
      [key]: value === null || value === '' ? '' : Number(value),
    }))
  }

  function buildPayload(): Record<string, string | number> {
    const data: Record<string, string | number> = {}
    if (monthEndDate) {
      data['monthEndDate'] = monthEndDate.endOf('month').format('YYYY-MM-DD')
    }
    formFields.forEach((field) => {
      if (field.subFields) {
        field.subFields.forEach((subField) => {
          const key = payloadKeyForSubField(field, subField)
          const value = answers[key] ?? ''
          data[key] = value === '' ? 0 : value
        })
      } else {
        const key = payloadKeyForField(field)
        const value = answers[key] ?? ''
        data[key] = value === '' ? 0 : value
      }
    })
    return data
  }

  function isFormValid(): boolean {
    if (formFields.length === 0) return false
    if (!monthEndDate) return false
    return formFields.every((field) => {
      if (field.subFields) {
        return field.subFields.every((subField) =>
          isValidNumericAnswer(answers[payloadKeyForSubField(field, subField)] ?? '', subField)
        )
      }
      return isValidNumericAnswer(answers[payloadKeyForField(field)] ?? '', field)
    })
  }

  async function handleSave() {
    if (!isFormValid()) {
      setShowValidationErrors(true)
      addToast('Please answer all questions!', 'error')
      return
    }

    const payload = buildPayload()
    try {
      if (isNew) {
        await saveMutation.mutateAsync(payload)
        addToast('Survey form submitted successfully', 'success')
      } else if (isEdit && selectedRow) {
        await updateMutation.mutateAsync({
          surveyFormId: selectedRow.surveyFormId,
          surveyFormData: payload,
        })
        addToast('Survey form updated successfully', 'success')
      }
      closeModal()
    } catch {
      addToast('Failed to save survey form', 'error')
    }
  }

  async function handleDelete() {
    if (!selectedRow || !isEdit) return
    try {
      await deleteMutation.mutateAsync(selectedRow.surveyFormId)
      addToast('Survey form deleted successfully', 'success')
      closeModal()
    } catch {
      addToast('Failed to delete survey form', 'error')
    }
  }

  const columns: ColumnsType<SurveyFormSubmission> = useMemo(
    () => [
      { title: 'User', dataIndex: 'fullName', key: 'fullName', sorter: (a, b) => a.fullName.localeCompare(b.fullName) },
      { title: 'Email', dataIndex: 'email', key: 'email' },
      { title: 'Month End Date', dataIndex: 'monthEndDate', key: 'monthEndDate', render: (v: string) => formatDate(v) },
      { title: 'Date Submitted', dataIndex: 'insertDate', key: 'insertDate', render: (v: string) => formatDate(v) },
      { title: 'Last Updated', dataIndex: 'updateDate', key: 'updateDate', render: (v: string | null) => formatDate(v) },
    ],
    []
  )

  const isSaving = saveMutation.isPending || updateMutation.isPending
  const isViewOnly = !isNew && !isEdit
  const modalTitle = isNew ? 'Submit New Survey Form' : isEdit ? 'Edit Survey Form' : 'View Survey Form'

  const modalFooter = isViewOnly
    ? [<Button key="close" onClick={closeModal}>Close</Button>]
    : [
        ...(isEdit
          ? [
              <Button
                key="delete"
                danger
                loading={deleteMutation.isPending}
                onClick={() => setDeleteConfirmOpen(true)}
              >
                Delete
              </Button>,
            ]
          : []),
        <Button key="cancel" onClick={closeModal}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={isSaving}
          disabled={!isFormValid()}
          onClick={handleSave}
        >
          Save
        </Button>,
      ]

  function renderInfoIcon(field: SurveyFormField) {
    const icon = <InfoCircleOutlined className={field.link ? 'text-blue-500 cursor-pointer mt-0.5' : 'text-gray-400 mt-0.5'} />
    return (
      <Tooltip title={field.description}>
        {field.link ? (
          <a href={field.link} target="_blank" rel="noopener noreferrer" onClick={(event) => event.stopPropagation()}>
            {icon}
          </a>
        ) : (
          icon
        )}
      </Tooltip>
    )
  }

  function renderNumberInput(key: string, config: NumberFieldConfig, className = 'w-full') {
    const answer = answers[key] ?? ''
    const value = answer === '' ? null : answer
    return (
      <InputNumber
        className={className}
        value={value}
        precision={config.decimalPlaces ?? 0}
        min={config.allowNegative === false ? 0 : undefined}
        status={getValidationStatus(answer, config, showValidationErrors)}
        disabled={isViewOnly}
        placeholder="Enter value"
        onChange={(val) => updateAnswer(key, val)}
        {...getNumberAddons(config)}
      />
    )
  }

  return (
    <div className="rounded-2xl border border-brand-100 shadow-card bg-gradient-to-br from-white to-rose-50/30">
      <div className="px-6 pt-5 pb-4 border-b border-brand-100 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Survey Forms</h1>
          <p className="text-sm text-gray-500 mt-1">Client Name: {clientName}</p>
        </div>
        {canSurveyAdd && (
          <Button type="primary" icon={<PlusOutlined />} loading={questionsLoading} disabled={questionsLoading || questionsError} onClick={openNewModal}>
            Submit New Form
          </Button>
        )}
      </div>

      <div className="p-4">
        {questionsError ? (
          <Alert
            type="error"
            message="Failed to load survey form questions"
            action={<Button size="small" onClick={() => refetchQuestions()}>Retry</Button>}
            showIcon
          />
        ) : isError ? (
          <Alert
            type="error"
            message="Failed to load survey forms"
            action={<Button size="small" onClick={() => refetchSubmissions()}>Retry</Button>}
            showIcon
          />
        ) : (
          <Table<SurveyFormSubmission>
            rowKey="surveyFormId"
            loading={isLoading || questionsLoading}
            columns={columns}
            dataSource={submissions}
            locale={{ emptyText: 'No survey forms found' }}
            pagination={{ showSizeChanger: true, pageSizeOptions: ['10', '25', '50'] }}
            onRow={(record) => ({
              onClick: () => {
                if (formFields.length > 0) openRowModal(record)
              },
              style: { cursor: formFields.length > 0 ? 'pointer' : 'default' },
            })}
          />
        )}
      </div>

      <Modal
        open={modalOpen}
        title={modalTitle}
        onCancel={closeModal}
        footer={modalFooter}
        width={980}
        destroyOnClose
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          {selectedRow && (
            <div className="mb-4 flex flex-wrap justify-between gap-2 text-sm text-gray-600">
              <span>User: {selectedRow.fullName}</span>
              <span>Date Submitted: {formatDate(selectedRow.insertDate)}</span>
            </div>
          )}

          <div className="mb-5">
            <div className="flex items-center gap-1 mb-2">
              <label className="text-sm font-medium text-gray-700">
                1. Month End Date of Reporting Period
              </label>
              <Tooltip title="Select the month-end date for the reporting period">
                <InfoCircleOutlined className="text-gray-400" />
              </Tooltip>
            </div>
            <DatePicker
              picker="month"
              value={monthEndDate}
              onChange={(val) => setMonthEndDate(val ? val.endOf('month') : null)}
              disabled={isViewOnly}
              status={showValidationErrors && !monthEndDate ? 'error' : undefined}
              className="w-full max-w-xs"
              placeholder="Select month"
            />
          </div>

          <div className="space-y-5">
            {formFields.map((field, i) => (
              <div key={field.label} className="border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">
                <div className="flex items-start gap-1 mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    {i + 2}. {field.label}
                  </span>
                  {renderInfoIcon(field)}
                </div>

                {field.subFields ? (
                  <div>
                    <span className="block text-xs text-gray-500 mb-2">
                      If not applicable put 0 as a placeholder
                    </span>
                    <div className="overflow-x-auto pb-1">
                      <div
                        className="grid gap-x-4 gap-y-3 min-w-full"
                        style={{ gridTemplateColumns: `repeat(${field.gridCols ?? 1}, minmax(120px, 1fr))` }}
                      >
                        {field.subFields.map((subField) => {
                          const key = payloadKeyForSubField(field, subField)
                          return (
                            <div key={key} className="min-w-0">
                              <label className="block text-xs text-gray-600 mb-1 truncate" title={subField.label}>
                                {subField.label}
                              </label>
                              {renderNumberInput(key, subField)}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  renderNumberInput(payloadKeyForField(field), field, 'w-full max-w-xs')
                )}
              </div>
            ))}
          </div>
        </div>
      </Modal>

      <Modal
        open={deleteConfirmOpen}
        title="Delete Survey Form"
        onCancel={() => setDeleteConfirmOpen(false)}
        onOk={handleDelete}
        okText="Delete"
        okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
      >
        Are you sure you want to delete this survey form submission?
      </Modal>
    </div>
  )
}
